// ─────────────────────────────────────────────────────────────────────────────
// INDUSTRY REGISTRY — the single place an industry is declared.
//
// Roadmap 5.11 promised that adding a vertical is configuration, not a new app.
// Since 2026-10-18 each industry is a PACK: one module in utils/industryPacks/
// holding everything a new company of that industry starts with, listed in
// INDUSTRY_PACKS below. Nothing outside the packs knows what any industry
// contains; everything reads it through the helpers in this file.
//
// A PACK:
//   id                     stable machine id, stored on companies/{id}.industry
//   name                   display name
//   isPublic               whether a signup answer with this id may seed it
//                          (utils/signupOptions.js) — false keeps it internal
//   icon                   optional { viewBox, paths } — see IndustryIcon, which
//                          draws nothing without one (and is rendered nowhere
//                          today)
//   buildDefaultJobTypes   () => JobType[]       extras.jobTypes: stages with
//                          categories, default folders, custom-field template
//   buildDefaultLeadSources() => LeadSource[]    extras.leadSources
//   buildDefaultCostCategories() => CostCategory[] extras.costCategories
//   buildDefaultCustomFields() => { jobs, clients, serviceTickets }
//                          extras.customFields. A job field may carry
//                          jobTypeIds, the types it shows on (none = all) —
//                          see utils/customFieldScope.js
//   buildDefaultPriceBook  () => { manufacturers } extras.priceBook — a pack
//                          that sells no manufactured product ships an EMPTY book
//   hiddenFeatures         optional. Feature Visibility keys a new company
//                          starts with hidden (companies/{id}.featureVisibility).
//                          Default: none.
//   copy                   optional. The words the first-run screens and
//                          Settings use — see industryCopy() for each one's
//                          place. A key a pack leaves out reads the default
//                          industry's.
//
// WHY BUILDER FUNCTIONS AND NOT FROZEN ARRAYS: a pack seeded in the browser
// mints fresh stage ids on every call (industryPacks/packBuilders.js) so each
// company owns its own. A single frozen array would hand every company the same
// object graph, so packs expose functions and callers invoke them at seed time.
//
// WHO READS THIS:
//   • services/companyService.js  — seeds a new company at signup, hidden
//                                   features included
//   • context/CompanyContext.js   — read-time seeding / migration of extras
//   • utils/jobTypesUtils.js      — normalizeJobTypes' default-industry fallback
//   • utils/signupOptions.js      — which pack a signup answer seeds
//   • the first-run copy: pages/SignupPage.js, components/GuidedOnboarding.js,
//     pages/Dashboard.js (the New Record panel)
//   • the Settings copy: pages/Settings.js (Job Types, Team Members), which
//     hands the job type editor its examples (components/JobTypeEditor.js)
//   • the company's own defaults: pages/Settings.js, components/JobFinancials.js,
//     components/ImportPage.js, components/GuidedOnboarding.js
//
// ADDING AN INDUSTRY: see BONEFLOW_VISION.md → "Industry Packs".
// ─────────────────────────────────────────────────────────────────────────────

import { WINDOW_DOOR_PACK, WINDOW_DOOR_INDUSTRY_ID } from './industryPacks/windowDoor';
import { SAAS_SUBSCRIPTION_PACK } from './industryPacks/saasSubscription';
import { FOOD_BEVERAGE_PACK, FOOD_BEVERAGE_INDUSTRY_ID } from './industryPacks/foodBeverage';
import { SAAS_INDUSTRY_ID } from './saasIndustryUtils';
import { buildEmptyPriceBook } from './priceBookUtils';

export { WINDOW_DOOR_INDUSTRY_ID, SAAS_INDUSTRY_ID, FOOD_BEVERAGE_INDUSTRY_ID };

// The industry a company falls back to when its `industry` field is missing.
// Every company created before the field existed is a Window & Door company.
export const DEFAULT_INDUSTRY_ID = WINDOW_DOOR_INDUSTRY_ID;

export const INDUSTRY_PACKS = [
  WINDOW_DOOR_PACK,
  // INTERNAL ONLY - not shown in public signup or industry selector
  SAAS_SUBSCRIPTION_PACK,
  FOOD_BEVERAGE_PACK,
];

// Resolve a pack by id. Falls back to the default industry so a company with a
// missing/unknown `industry` still gets sensible defaults rather than a crash —
// the same forgiving posture as findJobType/leadSourceNameById.
export function getIndustry(industryId) {
  return (
    INDUSTRY_PACKS.find((p) => p.id === industryId) ||
    INDUSTRY_PACKS.find((p) => p.id === DEFAULT_INDUSTRY_ID)
  );
}

export function getPublicIndustries() {
  return INDUSTRY_PACKS.filter((p) => p.isPublic);
}

export function isPublicIndustry(industryId) {
  return INDUSTRY_PACKS.some((p) => p.id === industryId && p.isPublic);
}

export function industryName(industryId) {
  return getIndustry(industryId).name;
}

export function industryIcon(industryId) {
  return getIndustry(industryId).icon;
}

// ── Seed helpers ─────────────────────────────────────────────────────────────
// Thin wrappers so callers never have to remember the pack shape. Each returns
// a FRESH structure, safe to mutate.

export function buildDefaultJobTypesForIndustry(industryId) {
  return getIndustry(industryId).buildDefaultJobTypes();
}

export function buildDefaultLeadSourcesForIndustry(industryId) {
  return getIndustry(industryId).buildDefaultLeadSources();
}

export function buildDefaultCostCategoriesForIndustry(industryId) {
  return getIndustry(industryId).buildDefaultCostCategories();
}

export function buildDefaultCustomFieldsForIndustry(industryId) {
  return getIndustry(industryId).buildDefaultCustomFields();
}

export function buildDefaultPriceBookForIndustry(industryId) {
  const pack = getIndustry(industryId);
  // Guard rather than assume: a pack without a book gets an empty one, not a
  // crash.
  return pack.buildDefaultPriceBook ? pack.buildDefaultPriceBook() : buildEmptyPriceBook();
}

// Everything a brand-new company's meta/extras document starts with — the one
// shape /signup writes (services/companyService.js).
export function buildSeedExtras(industryId) {
  return {
    jobTypes: buildDefaultJobTypesForIndustry(industryId),
    customFields: buildDefaultCustomFieldsForIndustry(industryId),
    pricingPresets: [],
    companyDocumentFields: [],
    costCategories: buildDefaultCostCategoriesForIndustry(industryId),
    leadSources: buildDefaultLeadSourcesForIndustry(industryId),
    priceBook: buildDefaultPriceBookForIndustry(industryId),
  };
}

// The Feature Visibility keys this industry starts with hidden.
export function hiddenFeaturesForIndustry(industryId) {
  return [...(getIndustry(industryId).hiddenFeatures || [])];
}

// The words the first-run screens and Settings use, for this industry:
//   companyNamePlaceholder  /signup, the Company Name field
//   pipelineIntro           guided onboarding, under "Set up your pipeline."
//   jobTypeConcept          guided onboarding, the (?) beside "Job Type"
//   jobNamePlaceholder      the New Record panel, the Job Name field
//   jobTypesIntro           Settings → Job Types, the line under the title
//   jobTypeNamePlaceholder  the job type editor's examples: Job Type Name,
//   stageNamePlaceholder      a stage's name,
//   folderNamePlaceholder     a document folder's name
//   permissionDescs         { permission key: description } for the
//                           permissions whose description names the trade
//                           (Settings → Team Members); every other one is in
//                           services/userService.js's PERMISSION_LABELS
export function industryCopy(industryId) {
  const base = getIndustry(DEFAULT_INDUSTRY_ID).copy;
  const own = getIndustry(industryId).copy || {};
  return {
    ...base,
    ...own,
    permissionDescs: { ...base.permissionDescs, ...(own.permissionDescs || {}) },
  };
}
